import { useState } from "react";
import { queryClient } from "@/lib/query-client";
import { useGmailOAuth } from "@/hooks/use-gmail-oauth";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";

interface GmailConnectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isReconnect?: boolean;
}

export default function GmailConnectModal({ open, onOpenChange, isReconnect = false }: GmailConnectModalProps) {
  const { openOAuthPopup } = useGmailOAuth();
  const { toast } = useToast();
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await fetch('/api/gmail/auth', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to get auth URL');
      const { authUrl } = await res.json();

      const result = await openOAuthPopup(authUrl);

      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['/api/gmail/accounts'] });
        queryClient.invalidateQueries({ queryKey: ['/api/gmail/emails/recent'] });
        queryClient.invalidateQueries({ queryKey: ['/api/inbox-scan/status'] });
        toast({
          title: "Email Connected",
          description: `Connected ${result.email || 'your email account'}`,
        });
        onOpenChange(false);
      } else {
        toast({
          title: "Connection Failed",
          description: result.reason || "Unknown error",
          variant: "destructive",
        });
      }
    } catch (e: any) {
      toast({
        title: "Connection Failed",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-gmail-connect">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {isReconnect ? 'Reconnect Email' : 'Connect Email'}
          </DialogTitle>
          <DialogDescription>
            {isReconnect
              ? 'Your email account has been disconnected or access was revoked. Reconnect to continue seeing emails on the dashboard.'
              : 'Connect your email account to see recent emails directly on the dashboard and speed up your workflow.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-gmail-modal-dismiss"
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleConnect}
            disabled={connecting}
            data-testid="button-gmail-modal-connect"
          >
            {connecting ? 'Connecting...' : isReconnect ? 'Reconnect Email' : 'Connect Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
